import Scenes from "../enums/Scenes";
import WeaponType from "../enums/WeaponType";
import Game from "../game/Game";
import WEAPONS from "../consts/WEAPONS";
import { TEXT_STYLES } from "../../utils/FontManager";
import { t } from "../../utils/i18n/index";
import { Sizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import { DEFAULT_SCALE } from "../consts/UI";
import { SPRITESHEET_KEYS, SPRITESHEET_FRAMES } from "../consts/SPRITESHEETS";

interface EvolutionData {
  nameKey: string;
  descriptionKey: string;
  iconName: string;
  bulletType: WeaponType;
  evolvesFrom: WeaponType[];
}

export default class EvolutionScene extends Phaser.Scene {
  private evolutionData: EvolutionData | null = null;
  private sourceIcons: Phaser.GameObjects.Image[] = [];
  private resultIcon: Phaser.GameObjects.Image | null = null;
  private animationComplete: boolean = false;
  private panel: Sizer;
  private scaleRatio: number;
  private container: Phaser.GameObjects.Container;
  private mask: Phaser.GameObjects.Graphics;
  private confettiEmitter: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor() {
    super({ key: Scenes.EvolutionScene });
  }

  init(data: { evolution: WeaponType }) {
    this.animationComplete = false;
    this.evolutionData = this.getEvolutionData(data.evolution);
    this.scaleRatio = DEFAULT_SCALE;
  }

  getEvolutionData(evolutionType: WeaponType): EvolutionData {
    // Get weapon data
    const weaponData = WEAPONS[evolutionType][0];

    if (weaponData) {
      return weaponData;
    } else {
      throw new Error(`Unknown evolution type: ${evolutionType}`);
    }
  }

  // Helper function to check if an icon is in any spritesheet
  private getSpritesheetInfo(
    iconName: string
  ): { key: string; frame: string } | null {
    // Check WEAPONS spritesheet
    const weaponFrames = Object.values(SPRITESHEET_FRAMES.WEAPONS);
    if (weaponFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.WEAPONS, frame: iconName };
    }

    // Check STATS spritesheet
    const statFrames = Object.values(SPRITESHEET_FRAMES.STATS);
    if (statFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.STATS, frame: iconName };
    }

    // Check PICKUPS spritesheet
    const pickupFrames = Object.values(SPRITESHEET_FRAMES.PICKUPS);
    if (pickupFrames.includes(iconName as any)) {
      return { key: SPRITESHEET_KEYS.PICKUPS, frame: iconName };
    }

    return null;
  }

  // Helper function to create an image with proper texture handling
  private createIconImage(iconName: string): Phaser.GameObjects.Image {
    const spritesheetInfo = this.getSpritesheetInfo(iconName);
    if (spritesheetInfo) {
      return this.add.image(0, 0, spritesheetInfo.key, spritesheetInfo.frame);
    } else {
      // Fall back to individual texture for non-spritesheet icons
      return this.add.image(0, 0, iconName);
    }
  }

  create() {
    if (!this.evolutionData) {
      console.error("No evolution data provided");
      this.returnToGame();
      return;
    }

    // Ensure main scene is paused and use FreezeSnapshot for consistency with other modals
    this.scene.pause(Scenes.MainScene);
    Game.Core.FreezeSnapshot(() => {
      this.setupUI();
    });
  }

  setupUI() {
    // Play treasure sound when evolution scene appears using pooled audio
    Game.Core.poolManager?.playTreasureMusic();

    const BASE_DEPTH = 1000;

    // Layer 1: Background mask - Dark overlay covering the entire screen
    this.mask = this.add.graphics().setScrollFactor(0);
    this.mask.fillStyle(0x000000, 0.8);
    this.mask.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    this.mask.setDepth(BASE_DEPTH);

    // Confetti will be created with depth between backdrop and dialog
    const CONFETTI_DEPTH = BASE_DEPTH + 5;

    // Create container for all UI elements with a high depth
    this.container = this.add.container(0, 0).setDepth(BASE_DEPTH + 10);

    // Dialog panel dimensions (match treasure scene)
    const dialogWidth = 345 * this.scaleRatio;
    const dialogHeight = 377 * this.scaleRatio;

    // Create panel using rexUI.add.sizer like in TreasureScene
    this.panel = this.rexUI.add.sizer({
      x: this.cameras.main.width / 2,
      y: this.cameras.main.height / 2,
      width: dialogWidth,
      height: dialogHeight,
      orientation: "vertical",
      anchor: {
        centerX: "center",
        centerY: "center",
      },
      space: {
        left: 20 * this.scaleRatio,
        right: 20 * this.scaleRatio,
        top: 30 * this.scaleRatio,
        bottom: 20 * this.scaleRatio,
        item: 10 * this.scaleRatio,
      },
    });

    // Dark background with rounded corners
    this.panel.addBackground(
      this.rexUI.add
        .roundRectangle(0, 0, 0, 0, 20 * this.scaleRatio, 0x1e2938)
        .setStrokeStyle(2 * this.scaleRatio, 0x141c27)
    );

    // Title
    const title = this.add.text(
      0,
      0,
      t("evolution.title") || "New Evolution!",
      {
        ...TEXT_STYLES.PIXEL,
        fontSize: `${22 * this.scaleRatio}px`,
        color: "#FFB935",
        align: "center",
        wordWrap: { width: dialogWidth - 40 * this.scaleRatio },
      }
    );
    this.panel.add(title, {
      padding: { top: 10 * this.scaleRatio, bottom: 10 * this.scaleRatio },
    });

    // Weapon name
    if (this.evolutionData) {
      const weaponName = this.add.text(0, 0, t(this.evolutionData.nameKey), {
        ...TEXT_STYLES.PIXEL,
        fontSize: `${20 * this.scaleRatio}px`,
        color: "#FFFFFF",
        align: "center",
      });
      this.panel.add(weaponName, {
        padding: { bottom: 10 * this.scaleRatio },
        align: "center",
      });
    }

    // Description
    if (this.evolutionData) {
      const description = this.add.text(
        0,
        0,
        t(this.evolutionData.descriptionKey),
        {
          ...TEXT_STYLES.REGULAR,
          fontSize: `${16 * this.scaleRatio}px`,
          color: "#DDDDDD",
          align: "center",
          wordWrap: { width: dialogWidth - 40 * this.scaleRatio },
        }
      );
      this.panel.add(description, {
        padding: { bottom: 20 * this.scaleRatio },
        align: "center",
      });
    }

    // Fixed height animation space - further reduced
    const animationHeight = 80 * this.scaleRatio; // Reduced from 100
    const animationSpace = this.rexUI.add.sizer({
      orientation: "vertical",
      height: animationHeight,
    });

    // Source icons container - positioned at top of animation space
    const sourceIconsContainer = this.rexUI.add.sizer({
      orientation: "horizontal",
      space: { item: 20 * this.scaleRatio },
    });

    // Add source weapon icons
    if (this.evolutionData) {
      console.log(
        "=> ~ EvolutionScene ~ this.evolutionData.sourceTypes.forEach ~ this.evolutionData:",
        this.evolutionData
      );

      this.evolutionData.evolvesFrom.forEach((sourceType) => {
        const sourceData = WEAPONS[sourceType][0];
        const iconName = sourceData.iconName;

        const icon = this.createIconImage(iconName).setScale(
          2 * this.scaleRatio
        );

        this.sourceIcons.push(icon);
        sourceIconsContainer.add(icon);
      });
    }

    // Set up result icon placeholder in center of animation space
    let resultIconPlaceholder = this.add.container(0, 0);
    if (this.evolutionData) {
      this.resultIcon = this.createIconImage(this.evolutionData.iconName)
        .setScale(0)
        .setVisible(false);

      resultIconPlaceholder.add(this.resultIcon);
    }

    // Add icons to animation space
    animationSpace.add(sourceIconsContainer, {
      align: "center",
      proportion: 0,
      expand: false,
    });

    animationSpace.add(resultIconPlaceholder, {
      align: "center",
      padding: { top: 5 * this.scaleRatio }, // Reduced padding between source and result
      proportion: 0,
      expand: false,
    });

    // Add animation space to panel with fixed height to prevent button displacement
    this.panel.add(animationSpace, {
      proportion: 0,
      expand: false,
      align: "center",
      padding: { bottom: 30 * this.scaleRatio }, // Increased padding between animation area and button
    });

    // Receive button
    const buttonWidth = dialogWidth - 40 * this.scaleRatio;
    const buttonHeight = 49 * this.scaleRatio;

    const receiveButton = this.rexUI.add
      .label({
        background: this.rexUI.add.roundRectangle(
          0,
          0,
          buttonWidth,
          buttonHeight,
          10 * this.scaleRatio,
          0x3498db
        ),
        text: this.add.text(0, 0, t("evolution.receiveButton") || "Receive", {
          ...TEXT_STYLES.REGULAR,
          fontSize: `${16 * this.scaleRatio}px`,
          color: "#FFFFFF",
        }),
        space: {
          left: 20 * this.scaleRatio,
          right: 20 * this.scaleRatio,
          top: 10 * this.scaleRatio,
          bottom: 10 * this.scaleRatio,
        },
        align: "center",
        width: buttonWidth,
        height: buttonHeight,
      })
      .setInteractive({ useHandCursor: true })
      .onClick(() => {
        if (this.animationComplete) {
          this.acceptEvolution();
        }
      });

    // Initially disable button until animation completes
    receiveButton.setAlpha(0.5);

    // Store button reference for later use
    (this as any).receiveButton = receiveButton;

    // Add button directly to panel with appropriate padding
    this.panel.add(receiveButton, {
      align: "center",
      expand: false,
      padding: { bottom: 20 * this.scaleRatio },
      proportion: 0,
    });

    this.panel.layout();
    this.container.add(this.panel);

    // Start animation sequence
    this.startEvolutionAnimation();
  }

  startEvolutionAnimation() {
    if (!this.resultIcon || this.sourceIcons.length === 0) return;

    const BASE_DEPTH = 1000;
    const PARTICLE_DEPTH = BASE_DEPTH + 6;

    // Get world positions for the icons (convert from sizer-relative to scene-relative)
    this.sourceIcons.forEach((icon) => {
      const worldXY = icon.getWorldTransformMatrix().transformPoint(0, 0);
      icon.x = worldXY.x;
      icon.y = worldXY.y;
      this.container.add(icon);
    });

    if (this.resultIcon) {
      const worldXY = this.resultIcon
        .getWorldTransformMatrix()
        .transformPoint(0, 0);
      this.resultIcon.x = worldXY.x;
      // Store the default Y position for the result icon from the sourceIcon's position
      const sourceIconY = this.sourceIcons[0]?.y || worldXY.y;
      this.resultIcon.y = sourceIconY;
      this.container.add(this.resultIcon);
    }

    // Create particles with high depth
    const particleConfig = {
      speed: { min: 20, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      blendMode: "ADD",
      lifespan: 800,
      quantity: 1,
      depth: PARTICLE_DEPTH,
    };

    // For each source icon, create a separate particle emitter
    this.sourceIcons.forEach((sourceIcon) => {
      const emitter = this.add
        .particles(sourceIcon.x, sourceIcon.y, "particle", particleConfig)
        .setDepth(PARTICLE_DEPTH);

      // Make the emitter follow the icon
      this.tweens.add({
        targets: emitter,
        x: function () {
          return sourceIcon.x;
        },
        y: function () {
          return sourceIcon.y;
        },
        duration: 100,
        repeat: -1,
      });
    });

    // Calculate center position - only X will be used to center horizontally
    const centerX = this.cameras.main.width / 2;

    // Animate source icons: scaling and then movement to center (horizontally only)
    this.sourceIcons.forEach((sourceIcon, index) => {
      // Add shine effect
      this.tweens.add({
        targets: sourceIcon,
        scaleX: 2.3 * this.scaleRatio,
        scaleY: 2.3 * this.scaleRatio,
        duration: 300,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: 2,
        delay: index * 200,
      });

      // Move to center horizontally only, keeping original Y position
      this.tweens.add({
        targets: sourceIcon,
        x: centerX,
        // y coordinate is not included, so it stays at its original position
        ease: "Power2",
        delay: 1000 + index * 100,
        duration: 800,
        onComplete: () => {
          if (index === this.sourceIcons.length - 1) {
            this.showResultIcon();
          }
        },
      });
    });
  }

  showResultIcon() {
    if (!this.resultIcon) return;

    // Fade out source icons
    this.sourceIcons.forEach((sourceIcon) => {
      this.tweens.add({
        targets: sourceIcon,
        alpha: 0,
        duration: 500,
      });
    });

    // Create full-screen flash effect
    const flash = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.cameras.main.width,
      this.cameras.main.height,
      0xffffff
    );
    flash.setOrigin(0.5, 0.5);
    flash.setAlpha(0);
    flash.setDepth(1100); // Ensure flash is on top of everything

    this.tweens.add({
      targets: flash,
      alpha: 0.9, // Slightly increased alpha for full-screen effect
      duration: 200,
      yoyo: true,
      onComplete: () => {
        flash.destroy();
        this.revealResultIcon();
      },
    });
  }

  revealResultIcon() {
    if (!this.resultIcon) return;

    // Play sound effect for the reveal using pooled audio
    Game.Core.poolManager?.playLevelUpMusic();

    // Make result icon visible and scale it up
    this.resultIcon.setVisible(true);

    // Ensure result icon is at the same height as the source icons were
    const centerX = this.cameras.main.width / 2;
    // Keep the Y position the same as already set in startEvolutionAnimation
    this.resultIcon.setPosition(centerX, this.resultIcon.y);

    this.tweens.add({
      targets: this.resultIcon,
      scale: 2.5 * this.scaleRatio,
      ease: "Back.out",
      duration: 800,
      onComplete: () => {
        // Add pulsing effect to the result icon
        this.tweens.add({
          targets: this.resultIcon,
          scale: { from: 2.5 * this.scaleRatio, to: 2.3 * this.scaleRatio },
          duration: 800,
          ease: "Sine.easeInOut",
          yoyo: true,
          repeat: -1,
        });

        // Enable receive button
        if ((this as any).receiveButton) {
          (this as any).receiveButton.setAlpha(1);
        }

        this.animationComplete = true;

        // Create confetti effect with high depth to be above all UI
        this.createConfetti();
      },
    });
  }

  createConfetti() {
    // Create confetti particles between backdrop and dialog
    if (!this.evolutionData) return;

    const CONFETTI_DEPTH = 1005; // Between backdrop (1000) and dialog (1010)

    // For confetti particles, we need to handle texture carefully
    let confettiTexture: string;
    let confettiFrame: string | undefined;

    const spritesheetInfo = this.getSpritesheetInfo(
      this.evolutionData.iconName
    );
    if (spritesheetInfo) {
      confettiTexture = spritesheetInfo.key;
      confettiFrame = spritesheetInfo.frame;
    } else {
      confettiTexture = this.evolutionData.iconName;
      confettiFrame = undefined;
    }

    const particleConfig: any = {
      x: { min: 0, max: this.cameras.main.width },
      y: -50,
      speedY: { min: 200, max: 400 },
      speedX: { min: -20, max: 20 },
      angle: { min: 0, max: 360 },
      rotate: { min: 0, max: 360 },
      scale: { min: 0.5 * this.scaleRatio, max: 1.5 * this.scaleRatio },
      lifespan: 3000,
      frequency: 50,
      quantity: 2,
      tint: [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff],
    };

    if (confettiFrame) {
      // For spritesheet textures, we need to specify the frame in the config
      particleConfig.frame = confettiFrame;
      this.confettiEmitter = this.add
        .particles(0, 0, confettiTexture, particleConfig)
        .setDepth(CONFETTI_DEPTH);
    } else {
      // For regular textures
      this.confettiEmitter = this.add
        .particles(0, 0, confettiTexture, particleConfig)
        .setDepth(CONFETTI_DEPTH);
    }
  }

  acceptEvolution() {
    // Add the evolved weapon to the player's arsenal
    if (this.evolutionData) {
      // Remove source weapons
      Game.Core.RemoveWeapon(this.evolutionData.evolvesFrom);

      // Add the evolution weapon
      Game.Core.AddWeapon(this.evolutionData.bulletType);

      // Add the evolution weapon to LevelUpFactory's weapon store
      // so it appears in future level-up options
      Game.Core.LevelUpFactory.addLateWeapon(this.evolutionData.bulletType);

      // Return to game
      this.returnToGame();
    }
  }

  returnToGame() {
    // Clean up particle emitters
    if (this.confettiEmitter) {
      this.confettiEmitter.destroy();
    }

    // Resume main scene and destroy this one
    this.scene.stop(Scenes.EvolutionScene);
    this.scene.resume(Scenes.MainScene);
  }
}
